// src/pages/NotFound.jsx
import React from "react";
import { Box, Typography, Button, Stack } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

const INK = "#2B211C";
const MUTED = "#75675E";
const ACCENT = "#A06E50";
const PAPER = "#F7F4EF";
const LINE = "#DCD4CC";

const MotionBox = motion(Box);

// Moved outside the component to keep it static and fix the useEffect dependency warning
const words = ["lost", "missing", "elsewhere"];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      delay,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const wordVariants = {
  initial: {
    opacity: 0,
    y: 28,
    filter: "blur(8px)",
  },
  animate: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -24,
    filter: "blur(8px)",
    transition: {
      duration: 0.35,
      ease: [0.4, 0, 1, 1],
    },
  },
};

const NotFound = () => {
  const [wordIndex, setWordIndex] = React.useState(0);

  React.useEffect(() => {
    const timer = window.setInterval(() => {
      setWordIndex((current) => (current + 1) % words.length);
    }, 2200);

    return () => window.clearInterval(timer);
  }, []);

  const handleHome = () => {
    window.location.href = "/";
  };

  const handleServices = () => {
    window.location.href = "/services";
  };

  const handleProjects = () => {
    window.location.href = "/projects";
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = "/";
    }
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: "90vh",
        bgcolor: 'transparent',
        color: INK,
        overflow: "hidden",
        px: { xs: 2, sm: 3, md: 5 },
        py: { xs: 2.5, md: 1.5 },
        mt: 12,
        "& .editorial-serif": {
          fontFamily: "Georgia, 'Times New Roman', serif",
        },
      }}
    >
      {/* Decorative vertical editorial lines */}
      <Box
        aria-hidden="true"
        sx={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.45,
          backgroundImage: `
            linear-gradient(
              to right,
              transparent calc(20% - .5px),
              ${LINE} 20%,
              transparent calc(20% + .5px)
            ),
            linear-gradient(
              to right,
              transparent calc(80% - .5px),
              ${LINE} 80%,
              transparent calc(80% + .5px)
            )
          `,
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          maxWidth: 1500,
          minHeight: "calc(80vh - 48px)",
          mx: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <MotionBox
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0}
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            pb: 2,
            borderBottom: `1px solid ${LINE}`,
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 900,
              letterSpacing: ".2em",
              textTransform: "uppercase",
            }}
          >
            WiYou! Tech Studios
          </Typography>

          <Typography
            sx={{
              display: { xs: "none", md: "block" },
              fontSize: 9,
              color: MUTED,
              letterSpacing: ".18em",
              textTransform: "uppercase",
            }}
          >
            Digital studio
          </Typography>

          <Typography
            sx={{
              justifySelf: "end",
              fontSize: 10,
              color: ACCENT,
              fontWeight: 900,
              letterSpacing: ".14em",
            }}
          >
            404 / 2026
          </Typography>
        </MotionBox>

        {/* Main composition */}
        <Box
          sx={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1fr) 640px",
            },
            alignItems: "center",
            gap: { xs: 7, md: 12 },
            py: { xs: 8, md: 10 },
          }}
        >
          {/* Visual side */}
          <MotionBox
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0.1}
            sx={{
              position: "relative",
              minHeight: { xs: 300, sm: 420, md: 560 },
              display: "flex",
              alignItems: "center",
            }}
          >
            {/* Number */}
            <Typography
              className="editorial-serif"
              aria-label="404"
              sx={{
                position: "relative",
                zIndex: 2,
                fontSize: {
                  xs: "8.5rem",
                  sm: "13rem",
                  md: "19rem",
                  lg: "22rem",
                },
                fontWeight: 700,
                lineHeight: 0.7,
                letterSpacing: "-.105em",
                color: INK,
                userSelect: "none",
              }}
            >
              4
            </Typography>

            {/* Custom zero */}
            <MotionBox
              aria-hidden="true"
              animate={{
                rotate: [0, 2, -2, 0],
                y: [0, -8, 3, 0],
              }}
              transition={{
                duration: 7,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              sx={{
                position: "relative",
                zIndex: 3,
                width: {
                  xs: 125,
                  sm: 190,
                  md: 275,
                  lg: 320,
                },
                height: {
                  xs: 125,
                  sm: 190,
                  md: 275,
                  lg: 320,
                },
                mx: { xs: -1, sm: -2, md: -3 },
                border: {
                  xs: "18px solid",
                  sm: "27px solid",
                  md: "38px solid",
                },
                borderColor: ACCENT,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: PAPER,
                boxShadow: `0 0 0 1px ${LINE}`,
              }}
            >
              <Box
                sx={{
                  width: "24%",
                  height: "24%",
                  bgcolor: INK,
                  borderRadius: "50%",
                }}
              />
            </MotionBox>

            <Typography
              className="editorial-serif"
              aria-hidden="true"
              sx={{
                position: "relative",
                zIndex: 2,
                fontSize: {
                  xs: "8.5rem",
                  sm: "13rem",
                  md: "19rem",
                  lg: "22rem",
                },
                fontWeight: 700,
                lineHeight: 0.7,
                letterSpacing: "-.105em",
                color: INK,
                userSelect: "none",
              }}
            >
              4
            </Typography>

            {/* Small coordinate-style label */}
            <Box
              sx={{
                position: "absolute",
                left: { xs: 5, md: 12 },
                bottom: { xs: 5, md: 12 },
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box sx={{ width: 30, height: 1, bgcolor: ACCENT }} />
              <Typography
                sx={{
                  fontSize: 9,
                  color: MUTED,
                  letterSpacing: ".16em",
                  textTransform: "uppercase",
                }}
              >
                Error / Page unavailable
              </Typography>
            </Box>
          </MotionBox>

          {/* Content */}
          <MotionBox
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0.25}
            sx={{
              borderTop: `1px solid ${LINE}`,
              pt: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 900,
                  color: ACCENT,
                  letterSpacing: ".2em",
                  textTransform: "uppercase",
                }}
              >
                Page not found
              </Typography>

              <Typography
                sx={{
                  fontSize: 10,
                  color: MUTED,
                  letterSpacing: ".12em",
                }}
              >
                404
              </Typography>
            </Box>

            <Typography
              component="h1"
              className="editorial-serif"
              sx={{
                fontSize: {
                  xs: "3.4rem",
                  sm: "4.4rem",
                  md: "5.1rem",
                },
                fontWeight: 700,
                lineHeight: 0.94,
                letterSpacing: "-.065em",
                mb: 3.5,
              }}
            >
              You seem to be
              <br />
              <Box
                component="span"
                sx={{
                  color: ACCENT,
                  display: "inline-flex",
                  minWidth: { xs: "4.1em", md: "4.3em" },
                  overflow: "hidden",
                  verticalAlign: "bottom",
                }}
              >
                <AnimatePresence mode="wait">
                  <motion.span
                    key={words[wordIndex]}
                    variants={wordVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    style={{ display: "inline-block" }}
                  >
                    {words[wordIndex]}
                  </motion.span>
                </AnimatePresence>
              </Box>
              .
            </Typography>

            <Typography
              sx={{
                color: MUTED,
                fontSize: { xs: 14, md: 15 },
                lineHeight: 1.9,
                maxWidth: 430,
                mb: 4.5,
              }}
            >
              This page doesn't exist at this address. It may have moved,
              changed, or the link may have taken an unexpected route.
            </Typography>

            {/* Primary actions */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              sx={{
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <Button
                onClick={handleHome}
                startIcon={<HomeRoundedIcon />}
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  bgcolor: INK,
                  color: PAPER,
                  borderRadius: 0,
                  px: 2.5,
                  py: 1.5,
                  minHeight: 50,
                  textTransform: "none",
                  fontWeight: 800,
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: ACCENT,
                    boxShadow: "none",
                    transform: "translateY(-2px)",
                  },
                  transition: "all .25s ease",
                }}
              >
                Back to home
              </Button>

              <Button
                onClick={handleServices}
                endIcon={<NorthEastRoundedIcon />}
                sx={{
                  border: `1px solid ${LINE}`,
                  color: INK,
                  borderRadius: 0,
                  px: 2.5,
                  py: 1.5,
                  minHeight: 50,
                  textTransform: "none",
                  fontWeight: 800,
                  "&:hover": {
                    borderColor: INK,
                    bgcolor: "rgba(43,33,28,.04)",
                  },
                }}
              >
                Explore services
              </Button>
            </Stack>

            <Button
              onClick={handleProjects}
              endIcon={<NorthEastRoundedIcon />}
              sx={{
                mt: 2.5,
                px: 0,
                minWidth: 0,
                color: MUTED,
                fontSize: 10,
                fontWeight: 900,
                letterSpacing: ".15em",
                textTransform: "uppercase",
                "&:hover": {
                  bgcolor: "transparent",
                  color: ACCENT,
                },
              }}
            >
              See what we've built
            </Button>
          </MotionBox>
        </Box>

        {/* Bottom navigation / context */}
        <MotionBox
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0.4}
          sx={{
            borderTop: `1px solid ${LINE}`,
            pt: 2,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr auto 1fr",
            },
            gap: 2,
            alignItems: "center",
          }}
        >
          <Button
            onClick={handleBack}
            startIcon={<ArrowBackRoundedIcon />}
            sx={{
              justifySelf: "start",
              px: 0,
              minWidth: 0,
              color: MUTED,
              fontSize: 10,
              fontWeight: 900,
              letterSpacing: ".13em",
              textTransform: "uppercase",
              "&:hover": {
                bgcolor: "transparent",
                color: INK,
              },
            }}
          >
            Go back
          </Button>

          <Typography
            sx={{
              display: { xs: "none", sm: "block" },
              textAlign: "center",
              fontSize: 9,
              color: MUTED,
              letterSpacing: ".17em",
              textTransform: "uppercase",
            }}
          >
            Strategy · Design · Engineering
          </Typography>

          <Typography
            sx={{
              justifySelf: { xs: "start", sm: "end" },
              fontSize: 9,
              color: MUTED,
              letterSpacing: ".14em",
              textTransform: "uppercase",
            }}
          >
            Jaipur · India
          </Typography>
        </MotionBox>
      </Box>
    </Box>
  );
};

export default NotFound;